import * as React from "react";

import { analytics } from "@shared/utils/analytics";
import styles from "./input.module.scss";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  analyticsName?: string;
  formName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, analyticsName, formName, onChange, onFocus, onBlur, name, children, dangerouslySetInnerHTML, ...props }, ref) => {
    const fieldName = analyticsName || name || "input";
    const form = formName || "form";

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        analytics.logFormChange(form, fieldName, {
          value: e.target.value,
          type,
          inputType: type
        });
        onChange?.(e);
      },
      [form, fieldName, type, onChange]
    );

    const handleFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        analytics.logUIAction(`Input Focus: ${fieldName}`, {
          formName: form,
          fieldName,
          type
        });
        onFocus?.(e);
      },
      [form, fieldName, type, onFocus]
    );

    const handleBlur = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        analytics.logUIAction(`Input Blur: ${fieldName}`, {
          formName: form,
          fieldName,
          type,
          value: e.target.value
        });
        onBlur?.(e);
      },
      [form, fieldName, type, onBlur]
    );

    return (
      <input
        type={type}
        className={`${styles.base}${className ? ` ${className}` : ""}`}
        ref={ref}
        name={name}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />
    );
  }
);

interface IconInputProps extends InputProps {
  leftIcon?: React.ReactElement;
  rightIcon?: React.ReactElement;
  containerClassName?: string;
}

const IconInput = React.forwardRef<HTMLInputElement, IconInputProps>(
  (
    {
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      className,
      containerClassName,
      ...props
    },
    ref
  ) => {
    const inputClasses = [
      LeftIcon && styles.inputWithLeftIcon,
      RightIcon && styles.inputWithRightIcon,
      className
    ].filter(Boolean).join(" ");

    return (
      <div className={`${styles.iconContainer}${containerClassName ? ` ${containerClassName}` : ""}`}>
        <Input
          ref={ref}
          className={inputClasses}
          {...props}
        />
        {LeftIcon && (
          <div className={styles.leftIcon}>
            {LeftIcon}
          </div>
        )}
        {RightIcon && (
          <div className={styles.rightIcon}>
            {RightIcon}
          </div>
        )}
      </div>
    );
  }
);

export { Input, IconInput };
